import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMPTY = { id: null, name: "", text: "", session_type: "", category: "general", stars: 5, published: true };
const inputCls = "w-full border border-[#E5E5E2] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]";

export default function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: open.name, text: open.text, session_type: open.session_type, category: open.category || "general", stars: open.stars, published: open.published };
      if (open.id) await api.put(`/admin/reviews/${open.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Saved");
      setOpen(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    load();
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#7A7A7A]">Your favorite client words — shown on the home page under «Kind words».</p>
        <button onClick={() => setOpen({ ...EMPTY })} data-testid="review-new"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 py-2.5 hover:bg-[#4A4A4A] shrink-0 ml-4">
          <Plus size={14} /> Add Review
        </button>
      </div>

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5E5E2] p-5 flex flex-wrap items-start gap-x-6 gap-y-2" data-testid={`review-row-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-serif text-base italic leading-relaxed">“{r.text}”</p>
              <p className="text-xs text-[#7A7A7A] mt-2">
                {r.name}{r.session_type ? ` · ${r.session_type}` : ""}
                <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#B5B5B0]">· {r.category || "general"}</span>
                <span className="ml-2 inline-flex items-center gap-0.5 text-[#C9A24B]">
                  {Array.from({ length: r.stars || 5 }).map((_, i) => <Star key={i} size={10} fill="currentColor" strokeWidth={0} />)}
                </span>
                {r.published === false && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#B5B5B0]">(hidden)</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOpen({ id: r.id, name: r.name, text: r.text, session_type: r.session_type || "", category: r.category || "general", stars: r.stars || 5, published: r.published !== false })}
                data-testid={`review-edit-${r.id}`}
                className="text-[11px] uppercase tracking-[0.12em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A] transition-colors">Edit</button>
              <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} aria-label="Delete"
                className="px-3 border border-[#E5E5E2] hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-10">No reviews yet — add your first one.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="review-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">{open.id ? "Edit Review" : "New Review"}</h3>
              <button onClick={() => setOpen(null)} data-testid="review-close"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="space-y-3" data-testid="review-form">
              <textarea value={open.text} onChange={(e) => setOpen({ ...open, text: e.target.value })} required rows={5}
                placeholder="Review text * — paste the client's exact words" className={`${inputCls} resize-y`} data-testid="review-text" />
              <div className="grid grid-cols-2 gap-3">
                <input value={open.name} onChange={(e) => setOpen({ ...open, name: e.target.value })} required
                  placeholder="Client name * — e.g. Sarah M." className={inputCls} data-testid="review-name" />
                <input value={open.session_type} onChange={(e) => setOpen({ ...open, session_type: e.target.value })}
                  placeholder="Session type — e.g. Newborn" className={inputCls} data-testid="review-session-type" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Show on page</label>
                <select value={open.category} onChange={(e) => setOpen({ ...open, category: e.target.value })}
                  data-testid="review-category" className={inputCls}>
                  <option value="general">Home (Kind words)</option>
                  <option value="learn">Learn page</option>
                  <option value="workshops">Workshops page</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Stars</label>
                <div className="flex gap-1" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setOpen({ ...open, stars: n })} data-testid={`review-star-${n}`}
                      className={`p-1 ${n <= open.stars ? "text-[#C9A24B]" : "text-[#E5E5E2]"}`}>
                      <Star size={20} fill="currentColor" strokeWidth={0} />
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 hover:border-[#1A1A1A] transition-colors" data-testid="review-published-wrap">
                <input type="checkbox" checked={open.published} onChange={(e) => setOpen({ ...open, published: e.target.checked })}
                  className="accent-[#1A1A1A] w-4 h-4" data-testid="review-published" />
                Show on the website
              </label>
              <button type="submit" data-testid="review-save"
                className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A]">
                {open.id ? "Save Changes" : "Add Review"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
